import { useEffect, useState } from 'react'
import { Menu } from 'lucide-react'
import clsx from 'clsx'
import { useStore } from '../../store/useStore'

interface Props {
  onMenuClick?: () => void
}

export default function Header({ onMenuClick }: Props) {
  const { kronosReady, kronosDevice } = useStore()
  const [currentTime, setCurrentTime] = useState(new Date())

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000)
    return () => clearInterval(timer)
  }, [])

  const formattedTime = currentTime.toLocaleTimeString('ja-JP', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
    timeZone: 'Asia/Tokyo',
  })

  const formattedDate = currentTime.toLocaleDateString('ja-JP', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    timeZone: 'Asia/Tokyo',
  })

  return (
    <header className="h-[60px] bg-trading-panel border-b border-trading-border flex items-center px-3 md:px-4 gap-2 md:gap-4">
      {/* Hamburger - mobile only */}
      <button
        className="md:hidden flex-none p-2 rounded text-trading-muted hover:text-trading-text hover:bg-trading-border/60 transition-colors"
        onClick={onMenuClick}
        aria-label="メニューを開く"
      >
        <Menu size={20} />
      </button>

      {/* Logo */}
      <div className="flex-none flex flex-col leading-tight">
        <span className="text-lg font-bold tracking-tight">
          <span
            style={{
              background: 'linear-gradient(90deg, #3b82f6, #06b6d4)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}
          >
            TradeVista
          </span>
          <span className="text-trading-red font-black">.</span>
          <span
            style={{
              background: 'linear-gradient(90deg, #06b6d4, #3b82f6)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}
          >
            JP
          </span>
        </span>
        <span className="hidden sm:block text-[10px] text-trading-muted tracking-widest">
          AI為替予想・マーケット分析
        </span>
      </div>

      {/* Center spacer */}
      <div className="flex-1" />

      {/* Right: status + clock */}
      <div className="flex-none flex items-center gap-2 md:gap-4">
        {/* AI status */}
        <div className="flex items-center gap-1.5 px-2 py-1.5 rounded-md bg-trading-bg border border-trading-border">
          <span
            className={clsx(
              'inline-block flex-none w-2 h-2 rounded-full',
              kronosReady ? 'bg-trading-green animate-pulse' : 'bg-trading-yellow animate-pulse',
            )}
          />
          <span className="hidden sm:inline text-xs text-trading-muted font-mono">
            {kronosReady ? '稼働中' : '起動中'}
          </span>
          <span
            className={clsx(
              'text-xs font-mono font-semibold px-1.5 py-0.5 rounded',
              kronosDevice.toUpperCase() === 'GPU'
                ? 'bg-trading-blue/20 text-trading-blue'
                : 'bg-trading-muted/20 text-trading-muted',
            )}
          >
            {kronosDevice.toUpperCase() || 'CPU'}
          </span>
        </div>

        {/* Clock (JST) - hide on small mobile */}
        <div className="hidden sm:block text-right">
          <div className="text-sm font-mono text-trading-text tabular-nums">{formattedTime} JST</div>
          <div className="text-[10px] font-mono text-trading-muted">{formattedDate}</div>
        </div>
      </div>
    </header>
  )
}
